// Home v2 — recent activity card. Presentational: rows are passed in so the
// card stays reusable. When empty it shows the empty state + a "See all" link
// that routes to the full Activity screen (real flow). Rich AccountOp
// humanization is owned by the existing Activity module, not duplicated here.
import React from 'react'
import { Pressable, View } from 'react-native'

import ActivityReceiveIcon from '@common/assets/svg/ActivityReceiveIcon'
import RightArrowIcon from '@common/assets/svg/RightArrowIcon'
import SendIcon from '@common/assets/svg/SendIcon'
import ShieldFilledIcon from '@common/assets/svg/ShieldFilledIcon'
import SwapIcon from '@common/assets/svg/SwapIcon'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'

import { ActivityKind, RADII } from '../tokens'

export type ActivityItem = {
  id: string
  kind: ActivityKind
  label: string
  time: string
  amount: string
  amountUsd?: string
  outgoing?: boolean
}

const ICON: Record<ActivityKind, typeof SendIcon> = {
  receive: ActivityReceiveIcon,
  send: SendIcon,
  shield: ShieldFilledIcon,
  unshield: GlobeIcon,
  swap: SwapIcon,
  other: SwapIcon
}

type Props = {
  items?: ActivityItem[]
  onSeeAll: () => void
  /** Caption under the title — must describe the window the caller filtered by. */
  subtitle?: string
}

const ActivityCard: React.FC<Props> = ({
  items = [],
  onSeeAll,
  subtitle = 'Latest transactions'
}) => {
  const { theme } = useTheme()

  return (
    <View
      style={{
        backgroundColor: theme.homeCardBackground as string,
        borderRadius: RADII.lg,
        padding: 16
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 16
        }}
      >
        <View>
          <Text fontSize={18} weight="semiBold">
            Recent activity
          </Text>
          <Text fontSize={12} appearance="secondaryText">
            {subtitle}
          </Text>
        </View>
        <Pressable
          onPress={onSeeAll}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
        >
          <Text fontSize={14} weight="semiBold" color={theme.linkText as string}>
            See all
          </Text>
          <RightArrowIcon width={14} height={14} color={theme.linkText as string} />
        </Pressable>
      </View>

      {items.length === 0 ? (
        <View
          style={{
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: theme.homeCardBorder as string,
            borderRadius: RADII.md,
            paddingVertical: 24,
            alignItems: 'center'
          }}
        >
          <Text fontSize={14} weight="semiBold">
            No recent activity
          </Text>
          <Text fontSize={12} appearance="secondaryText" style={{ marginTop: 4 }}>
            Your transactions will show up here
          </Text>
        </View>
      ) : (
        <View style={{ gap: 16 }}>
          {items.map((item) => {
            const Icon = ICON[item.kind]
            return (
              <View key={item.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: theme.tertiaryBackground as string,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon width={20} height={20} color={theme.iconSecondary as string} />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text fontSize={14} weight="semiBold" numberOfLines={1}>
                    {item.label}
                  </Text>
                  <Text fontSize={12} appearance="secondaryText">
                    {item.time}
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text fontSize={14} weight="semiBold">
                    {item.outgoing ? '-' : '+'}
                    {item.amount}
                  </Text>
                  {!!item.amountUsd && (
                    <Text fontSize={12} appearance="secondaryText">
                      {item.amountUsd}
                    </Text>
                  )}
                </View>
              </View>
            )
          })}
        </View>
      )}
    </View>
  )
}

export default React.memo(ActivityCard)
